/**
 * Regras puras do chat rico (sem React): formatação, velocidade do áudio, cor do autor e
 * rótulos. Ficam separadas para serem testadas sem renderizar a tela.
 */
import type { ChatMessage } from "../../api/ai-types.js";

/** Velocidades do player de áudio, na ordem em que o botão alterna. */
export const AUDIO_SPEEDS = [1, 1.5, 2] as const;
export type AudioSpeed = (typeof AUDIO_SPEEDS)[number];

export function nextAudioSpeed(current: number): AudioSpeed {
  const index = AUDIO_SPEEDS.indexOf(current as AudioSpeed);
  return AUDIO_SPEEDS[(index + 1) % AUDIO_SPEEDS.length] ?? 1;
}

export function speedLabel(speed: number): string {
  return `${speed}x`;
}

/** 0:08, 1:05, 12:30. Entrada inválida vira "0:00". */
export function formatDuration(seconds: number | null | undefined): string {
  const total = typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Reações rápidas oferecidas ao passar o mouse na mensagem. */
export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "🙏", "🔥"] as const;

/** Cor estável por autor (o mesmo nome sempre tem a mesma cor), com contraste bom nos dois temas. */
export function authorColor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  const hues = [8, 28, 150, 190, 215, 265, 300, 335];
  return `hsl(${hues[hash % hues.length]} 62% 42%)`;
}

/** Quem "fala" na linha: nome do autor no grupo/1:1, "Você" para a loja, "Assistente (IA)" para a IA. */
export function senderLabel(message: Pick<ChatMessage, "sender" | "fromMe" | "author">, fallback: string): string {
  if (message.sender === "ai") return "Assistente (IA)";
  if (message.fromMe || message.sender === "human") return "Você";
  if (message.sender === "system") return "Sistema";
  return message.author?.name ?? fallback;
}

/** Texto curto e acessível do estado de entrega (some se não houver). */
export function statusLabel(status: ChatMessage["status"] | undefined): string | null {
  switch (status) {
    case "pending": return "Enviando";
    case "sent": return "Enviada";
    case "delivered": return "Entregue";
    case "read": return "Lida";
    case "played": return "Ouvida";
    case "failed": return "Falhou";
    default: return null;
  }
}

export function statusGlyph(status: ChatMessage["status"] | undefined): string {
  switch (status) {
    case "pending": return "…";
    case "sent": return "✓";
    case "delivered": case "read": case "played": return "✓✓";
    case "failed": return "!";
    default: return "";
  }
}

/** Hora do dia; outro dia mostra dia/mês e hora. */
export function messageTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return date.toDateString() === now.toDateString() ? time : `${date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${time}`;
}

/** Rótulo do separador de dia ("Hoje", "Ontem" ou a data). */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diff = Math.round((new Date(now.toDateString()).getTime() - new Date(date.toDateString()).getTime()) / 86_400_000);
  if (diff === 0) return "Hoje";
  if (diff === 1) return "Ontem";
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

/** Prévia para a lista: usa a última mensagem carregada ou, na lista sem histórico, a prévia do servidor. */
export function conversationPreview(input: { messages: Array<{ sender: string; text: string; messageType?: string }>; lastMessagePreview?: string | null }): string {
  const last = input.messages.at(-1);
  if (last) {
    const prefix = last.sender === "customer" ? "" : last.sender === "ai" ? "IA: " : "Você: ";
    const body = last.text || ({ image: "📷 Foto", video: "🎥 Vídeo", audio: "🎤 Áudio", sticker: "Figurinha", document: "📄 Documento", location: "📍 Localização" } as Record<string, string>)[last.messageType ?? ""] || "Mensagem";
    return `${prefix}${body}`;
  }
  return input.lastMessagePreview ?? "Sem mensagens";
}

/** A tela nunca mostra JID: se algum texto de exibição ainda o carrega, cai no número. */
export function stripJid(label: string): string {
  return label.replace(/@(?:s\.whatsapp\.net|c\.us|lid|g\.us)\b/g, "");
}
